import { liftForDarkSurface } from '../utils/color.utils';
import {
  DARK_DEFAULT_COLORS,
  LIGHT_DEFAULT_COLORS,
  PaletteColors,
  findPaletteByColors
} from '../pages/Preferences/preferences.palettes';

export interface ThemeColorStore {
  light?: PaletteColors;
  dark?: PaletteColors;
}

export function readThemeColorStore(uiPreferences: unknown): ThemeColorStore {
  if (!uiPreferences || typeof uiPreferences !== 'object') return {};
  const raw = (uiPreferences as { theme_colors?: ThemeColorStore }).theme_colors;
  if (!raw || typeof raw !== 'object') return {};
  return {
    light: pairFromUnknown(raw.light),
    dark: pairFromUnknown(raw.dark)
  };
}

export function writeThemeColorStore(
  uiPreferences: unknown,
  store: ThemeColorStore
): Record<string, unknown> {
  const base =
    uiPreferences && typeof uiPreferences === 'object'
      ? { ...(uiPreferences as Record<string, unknown>) }
      : {};
  return { ...base, theme_colors: store };
}

export function rememberModeColors(
  store: ThemeColorStore,
  dark: boolean,
  colors: PaletteColors
): ThemeColorStore {
  return { ...store, [dark ? 'dark' : 'light']: colors };
}

export function colorsForMode(
  dark: boolean,
  primary?: string,
  secondary?: string,
  store: ThemeColorStore = {}
): PaletteColors {
  const saved = dark ? store.dark : store.light;
  if (saved?.primary && saved?.secondary) return saved;

  const matched = findPaletteByColors(primary, secondary);
  if (matched) return dark ? matched.dark : matched.light;

  const fallback = dark ? DARK_DEFAULT_COLORS : LIGHT_DEFAULT_COLORS;
  if (!primary || !secondary) return fallback;
  if (dark) {
    return {
      primary: liftForDarkSurface(primary),
      secondary: liftForDarkSurface(secondary)
    };
  }
  return { primary, secondary };
}

function pairFromUnknown(value: unknown): PaletteColors | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const pair = value as Partial<PaletteColors>;
  if (typeof pair.primary !== 'string' || typeof pair.secondary !== 'string') return undefined;
  return { primary: pair.primary, secondary: pair.secondary };
}
